/*
 * MigrationExport.ts (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all it's contents are proprietary and confidential.
 *
 * @file MigrationExport.ts
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

/**
 * The one-off export this store emits so the new Abstract Store can be seeded from it.
 *
 * The wire format is **NDJSON**, not one JSON document: one `{ kind, data }` record per line,
 * opened by a `header` record and closed by a `manifest` record. The importer validates a line at
 * a time, which a materialised document cannot support, and the manifest being *last* is what
 * makes a truncated download detectable — a reader that reaches end-of-stream without one refuses
 * the file rather than importing three quarters of a customer base and reporting success.
 *
 * These interfaces are shared because the export screen renders the summary and the importer on
 * the other side keys on the header; both halves have to agree on the spelling of every field, and
 * two copies of that agreement is one that drifts.
 */

/** Bumped whenever the record shape changes in a way an older importer could not read. */
export const MIGRATION_EXPORT_FORMAT_VERSION = 1;

/**
 * The record kinds the export emits, in the order it emits them.
 *
 * The order is not cosmetic: the importer stages rows as they arrive and the mapping panel is
 * built from applications, products and tax rates, so those lead. Nothing in the format *requires*
 * it — the importer stages every row before it maps anything — but an export that led with four
 * thousand transactions would leave the screen unable to say what it was looking at until the last
 * line landed.
 */
export enum MigrationExportRecordKind {
  application = 'application' /**< Legacy `Application` — becomes an Application and a Seller. */,
  product = 'product' /**< Legacy `Product` — becomes a Product, a Price and its amounts. */,
  taxRate = 'tax_rate' /**< A `Store.countries[]` entry — becomes a TaxRate. */,
  discount = 'discount' /**< A `Store.discounts[]` entry — becomes a Coupon. */,
  customer = 'customer' /**< Legacy `Customer` — one document per *purchase*, not per person. */,
  userCache = 'user_cache' /**< A live `UserCache` row, so the importer can resolve an email to a user. */,
  subscription = 'subscription' /**< Legacy `Subscription` — five populations, see §4.2. */,
  transaction = 'transaction' /**< Legacy `Transaction` — becomes an Order and/or an Invoice. */
}

/** What the exporting store says about itself, carried on the first line. */
export interface IMigrationExportSource {
  store: string /**< The store's own display name, so an operator can tell two exports apart. */;
  baseURL?: string | null /**< Where the export came from. */;
  braintreeEnvironment?: string | null /**< `Sandbox` or `Production`. */;
  /**
   * The merchant the subscription ids in this file belong to.
   *
   * The importer refuses a batch whose merchant does not match the configured Braintree provider.
   * Importing an export taken against a different merchant leaves the store holding subscription
   * ids belonging to somebody else's account, and every later provider call fails against an
   * object it cannot see — which reads as a broken integration rather than as a wrong file.
   */
  braintreeMerchantID: string;
  braintreeMerchantAccountID?: string | null /**< The account the money settled into. */;
}

/** The first record of the file. */
export interface IMigrationExportHeader {
  formatVersion: number;
  exportedAt: string /**< ISO-8601, the server's clock. */;
  source: IMigrationExportSource;
}

/** The last record of the file. Its absence is what a truncated download is detected by. */
export interface IMigrationExportManifest {
  counts: Record<string, number> /**< Records actually written, per kind. */;
}

/**
 * The five counts that each block the cutover differently (§4.3), plus the two identity gaps (§3).
 *
 * They are reported *before* the export runs, because every one of them is work somebody has to do
 * in this store — a decision owed to a customer, a licence never created, a seat change already
 * charged for — and none of them can be carried across. A screen that only offered a download
 * button would let an operator take the export with all seven outstanding and find out afterwards.
 */
export interface IMigrationExportDrainCounts {
  pendingRefundRequests: number /**< Refund requests awaiting an admin decision. */;
  pendingSeatUpdates: number /**< Seat changes charged for and never sent to the licence backend. */;
  unfulfilledTransactions: number /**< Paid transactions whose first licence was never created. */;
  paidSubscriptionsWithoutBraintreeID: number /**< Blocking per row: nothing to check local state against. */;
  transactionsWithoutUserUUID: number /**< Imported against a placeholder unless an email resolves. */;
  manualTransactionsWithoutUserUUID: number /**< The subset of the above that an admin created by hand. */;
  customersWithoutUserUUID: number /**< Customer documents carrying no AbstractID identity. */;
}

/** What the screen shows before the button is pressed. */
export interface IMigrationExportSummary {
  source: IMigrationExportSource;
  counts: Record<string, number> /**< Records the export *would* write, per kind. */;
  earliestTransactionDate?: string | null /**< ISO-8601. */;
  latestTransactionDate?: string | null /**< ISO-8601. */;
  braintreePlanIDs: string[] /**< Distinct plan ids across live products, for the mapping screen. */;
  drain: IMigrationExportDrainCounts;
  staleUserCacheRows: number /**< Live rows older than the cache's own TTL; force a sync first (§3). */;
  /**
   * Transactions carrying the PDF this store issued them.
   *
   * They travel as a **second** download rather than inside the NDJSON above. Roughly a hundred
   * kilobytes each across thousands of transactions is hundreds of megabytes of base64 inside a
   * single JSON string, which has to exist whole before it can be parsed — past a normal
   * container's heap and near V8's string ceiling. One document per line is the same argument
   * applied one level down: each line is one PDF, and no line is ever the whole archive.
   */
  invoiceDocuments: number;
}

/**
 * One legacy invoice PDF, as a line of the document stream.
 *
 * The stream opens with the same header the record export does and closes with the same manifest,
 * so a truncated download is detected the same way — the reader ends without one and refuses.
 */
export interface IMigrationExportDocument {
  sourceID: string /**< The legacy Mongo transaction `_id` the document belongs to. */;
  templateVersion?: number | null /**< Which invoice template rendered it. */;
  bytes: string /**< The PDF, base64. */;
}

/** A short-lived, single-use ticket for the plain browser download. */
export interface IMigrationExportToken {
  token: string;
  expiresAt: string /**< ISO-8601. */;
}
